import React, { useState } from "react";
import { X, Upload, ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const emptySite = {
  name: "",
  initials: "",
  tagline: "",
  heading: "",
  subtext: "Tap to open WhatsApp and start chatting instantly",
  whatsappUrl: "",
  telegramUrl: "",
  downloadUrl: "",
  features: ["", "", ""],
  logo: "",
  accentColor: "#f6c453",
  active: false,
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-medium text-white/60 mb-1.5 block">{label}</label>
    {children}
  </div>
);

const inputCls =
  "w-full bg-[#12131f] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-[#25D366] transition-colors";

const SiteForm = ({ open, onClose, onSave, initial }) => {
  const [form, setForm] = useState(initial ? { ...emptySite, ...initial } : emptySite);
  const [uploadError, setUploadError] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    setForm(initial ? { ...emptySite, ...initial } : emptySite);
    setUploadError("");
  }, [initial, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFeature = (i, v) =>
    setForm((f) => {
      const features = [...(f.features || ["", "", ""])];
      features[i] = v;
      return { ...f, features };
    });

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError("");
    if (!file.type.startsWith("image/")) {
      setUploadError("Please select an image file");
      return;
    }
    if (file.size > 1024 * 1024) {
      setUploadError("Image must be under 1MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      initials: (form.initials || form.name.slice(0, 2)).toUpperCase(),
      heading: form.heading || `Connect with ${form.name} on WhatsApp`,
      features: (form.features || []).filter(Boolean),
    };
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1b1d2b] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{initial ? "Edit Site" : "Add Site"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          {/* Logo + accent */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 shrink-0 rounded-full p-[2px]" style={{ background: form.accentColor }}>
              <div className="badge-inner rounded-full w-full h-full flex items-center justify-center overflow-hidden">
                {form.logo ? (
                  <img src={form.logo} alt="logo" className="w-full h-full object-cover rounded-full" />
                ) : (
                  <span className="font-display font-extrabold text-sm" style={{ color: form.accentColor }}>
                    {(form.initials || form.name.slice(0, 2) || "AK").toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex-1 space-y-2">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-white bg-white/10 hover:bg-white/15 px-3 py-2 rounded-lg cursor-pointer transition-colors">
                <Upload className="w-4 h-4" /> Upload Logo
                <input type="file" accept="image/*" onChange={handleLogo} className="hidden" />
              </label>
              {form.logo && (
                <button type="button" onClick={() => set("logo", "")} className="ml-2 text-xs text-red-400 hover:underline">
                  Remove
                </button>
              )}
              {uploadError && <p className="text-red-400 text-xs">{uploadError}</p>}
              <p className="text-white/40 text-[11px] flex items-center gap-1"><ImageIcon className="w-3 h-3" /> PNG/JPG, under 1MB. Optional.</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Brand Name">
                <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} required />
              </Field>
            </div>
            <Field label="Initials">
              <input className={inputCls} value={form.initials} maxLength={3} onChange={(e) => set("initials", e.target.value)} placeholder="AK" />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3 items-end">
            <div className="col-span-2">
              <Field label="Tagline">
                <input className={inputCls} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} />
              </Field>
            </div>
            <Field label="Accent Colour">
              <div className="flex items-center gap-2">
                <input type="color" value={form.accentColor} onChange={(e) => set("accentColor", e.target.value)} className="w-10 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer p-0.5" />
                <input className={inputCls} value={form.accentColor} onChange={(e) => set("accentColor", e.target.value)} />
              </div>
            </Field>
          </div>

          <Field label="Heading (optional)">
            <input className={inputCls} value={form.heading} onChange={(e) => set("heading", e.target.value)} placeholder="Auto-generated if empty" />
          </Field>
          <Field label="Sub Text">
            <input className={inputCls} value={form.subtext} onChange={(e) => set("subtext", e.target.value)} />
          </Field>
          <Field label="WhatsApp URL">
            <input className={inputCls} value={form.whatsappUrl} onChange={(e) => set("whatsappUrl", e.target.value)} placeholder="https://wa.me/91..." required />
          </Field>
          <Field label="Telegram URL">
            <input className={inputCls} value={form.telegramUrl} onChange={(e) => set("telegramUrl", e.target.value)} placeholder="https://t.me/yourchannel" />
          </Field>
          <Field label="Download URL">
            <input className={inputCls} value={form.downloadUrl} onChange={(e) => set("downloadUrl", e.target.value)} placeholder="https://..." />
          </Field>
          <Field label="Feature Badges (3)">
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map((i) => (
                <input key={i} className={inputCls} value={(form.features || [])[i] || ""} onChange={(e) => setFeature(i, e.target.value)} placeholder={`Feature ${i + 1}`} />
              ))}
            </div>
          </Field>
          <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
            <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#25D366] w-4 h-4" />
            Set as active site (shown on landing page)
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg font-semibold text-sm bg-white/10 hover:bg-white/15 transition-colors">
              Cancel
            </button>
            <button type="submit" className="flex-1 py-2.5 rounded-lg font-semibold text-sm bg-[#25D366] hover:bg-[#1fb757] transition-colors">
              Save
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { X };
export default SiteForm;
